import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faCalendarAlt,
    faSpinner,
    faChevronDown,
    faChevronUp,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';

const CreatePaymentBatch = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [surveys, setSurveys] = useState([]);
    const [loadingSurveys, setLoadingSurveys] = useState(true);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [dateError, setDateError] = useState('');

    // Utility function to scroll to top and show error
    const showErrorAndScroll = (errorMessage) => {
        setError(errorMessage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Get yesterday's date in YYYY-MM-DD format (max allowed end date)
    const getMaxEndDate = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const year = yesterday.getFullYear();
        const month = String(yesterday.getMonth() + 1).padStart(2, '0');
        const day = String(yesterday.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [formData, setFormData] = useState({
        survey_ids: [], // Changed to array for multiple surveys
        period_start: '',
        period_end: '',
    });

    useEffect(() => {
        fetchSurveys();
    }, []);

    const fetchSurveys = async () => {
        try {
            setLoadingSurveys(true);
            console.log('Fetching surveys...');
            // Use /cati/surveys endpoint with include_all=true to get all surveys (CATI + F2F)
            // This is needed for payment batch creation where we need to show all surveys
            const response = await api.get('/cati/surveys', {
                params: {
                    include_all: 'true'
                }
            });
            console.log('Surveys API response:', response.data);
            if (response.data.success) {
                console.log('Surveys data:', response.data.data);
                setSurveys(response.data.data || []);
            } else {
                console.error('API returned success: false', response.data);
            }
        } catch (err) {
            console.error('Error fetching surveys:', err);
            console.error('Error response:', err.response?.data);
        } finally {
            setLoadingSurveys(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newFormData = {
                ...prev,
                [name]: value
            };
            
            // Validate dates in real-time
            if (name === 'period_start' || name === 'period_end') {
                const today = getTodayDate();
                
                // Check if start date is today
                if (name === 'period_start' && value === today) {
                    setDateError('La date de début ne peut pas être aujourd\'hui. Veuillez sélectionner une date antérieure pour garantir que tous les travaux de la journée sont inclus.');
                    newFormData.period_start = '';
                    return newFormData;
                }
                
                // Check if end date is today
                if (name === 'period_end' && value === today) {
                    setDateError('La date de fin ne peut pas être aujourd\'hui. Veuillez sélectionner une date antérieure pour garantir que tous les travaux de la journée sont inclus.');
                    newFormData.period_end = '';
                    return newFormData;
                }
                
                if (newFormData.period_start && newFormData.period_end) {
                    const startDate = new Date(newFormData.period_start);
                    const endDate = new Date(newFormData.period_end);
                    
                    if (endDate < startDate) {
                        setDateError('La date de fin doit être postérieure ou égale à la date de début');
                    } else if (newFormData.period_start === today) {
                        setDateError('La date de début ne peut pas être aujourd\'hui. Veuillez sélectionner une date antérieure pour garantir que tous les travaux de la journée sont inclus.');
                    } else if (newFormData.period_end === today) {
                        setDateError('La date de fin ne peut pas être aujourd\'hui. Veuillez sélectionner une date antérieure pour garantir que tous les travaux de la journée sont inclus.');
                    } else {
                        setDateError('');
                    }
                } else {
                    setDateError('');
                }
                
                // If start date changes and end date is before it, clear end date
                if (name === 'period_start' && newFormData.period_end) {
                    const startDate = new Date(value);
                    const endDate = new Date(newFormData.period_end);
                    if (endDate < startDate) {
                        newFormData.period_end = '';
                        setDateError('La date de fin a été réinitialisée car elle était antérieure à la date de début');
                    }
                }
            }
            
            return newFormData;
        });
        setError('');
    };


    const validateForm = () => {
        if (formData.survey_ids.length === 0) {
            showErrorAndScroll('Veuillez sélectionner au moins une enquête');
            return false;
        }

        if (!formData.period_start) {
            showErrorAndScroll('Veuillez sélectionner la date de début');
            return false;
        }

        if (!formData.period_end) {
            showErrorAndScroll('Veuillez sélectionner la date de fin');
            return false;
        }

        // Check for date validation error
        if (dateError) {
            showErrorAndScroll(dateError);
            return false;
        }

        const startDate = new Date(formData.period_start);
        const endDate = new Date(formData.period_end);
        const today = getTodayDate();

        // Check if start date is today
        if (formData.period_start === today) {
            showErrorAndScroll('La date de début ne peut pas être aujourd\'hui. Veuillez sélectionner une date antérieure pour garantir que tous les travaux de la journée sont inclus.');
            return false;
        }

        // Check if end date is today
        if (formData.period_end === today) {
            showErrorAndScroll('La date de fin ne peut pas être aujourd\'hui. Veuillez sélectionner une date antérieure pour garantir que tous les travaux de la journée sont inclus.');
            return false;
        }

        if (endDate < startDate) {
            showErrorAndScroll('La date de fin doit être postérieure ou égale à la date de début');
            return false;
        }

        return true;
    };

    const checkOverlappingBatches = async () => {
        if (!formData.period_start || !formData.period_end || formData.survey_ids.length === 0) {
            return null;
        }

        try {
            const response = await api.get('/payments', {
                params: {
                    period_start: formData.period_start,
                    period_end: formData.period_end,
                    survey_ids: formData.survey_ids,
                    status: 'draft,validated,locked'
                }
            });

            if (response.data.success && response.data.data.data.length > 0) {
                const overlappingBatch = response.data.data.data[0];
                const overlappingSurveys = overlappingBatch.survey_ids || [];
                const currentSurveys = formData.survey_ids || [];
                const sharedSurveys = overlappingSurveys.filter(id => currentSurveys.includes(id));
                
                // Get survey names for the shared surveys
                const surveyNames = sharedSurveys.map(id => {
                    const survey = surveys.find(s => s.id === id);
                    return survey ? survey.title : `ID ${id}`;
                });
                
                let surveyMessage;
                if (surveyNames.length === 1) {
                    surveyMessage = `l'enquête "${surveyNames[0]}"`;
                } else {
                    surveyMessage = `les enquêtes "${surveyNames.join('", "')}"`;
                }
                
                return `Un lot de paiement existe déjà avec ${surveyMessage} et des dates qui se chevauchent (Lot #${overlappingBatch.id}: ${overlappingBatch.period_start} - ${overlappingBatch.period_end}).`;
            }
        } catch (err) {
            console.error('Error checking overlapping batches:', err);
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Check for overlapping batches before submitting
        const overlapError = await checkOverlappingBatches();
        if (overlapError) {
            showErrorAndScroll(overlapError);
            return;
        }

        try {
            setLoading(true);
            setError('');

            const payload = {
                survey_ids: formData.survey_ids,
                period_start: formData.period_start,
                period_end: formData.period_end,
            };

            const response = await api.post('/payments', payload);

            if (response.data.success) {
                setSuccessMessage('Lot de paiement créé avec succès !');
                setShowSuccessModal(true);
                
                // Redirect to batch detail page after 1.5 seconds
                setTimeout(() => {
                    navigate(`/payments/${response.data.data.id}`);
                }, 1500);
            }
        } catch (err) {
            console.error('Error creating payment batch:', err);
            showErrorAndScroll(
                err.response?.data?.message || 
                'Erreur lors de la création du lot de paiement'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                        Créer un Lot de Paiement
                    </h1>
                    <p className="text-gray-600">
                        Créez un nouveau lot de paiement hebdomadaire pour un rôle spécifique
                    </p>
                </div>

                {/* Form */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:p-8 border border-gray-200">
                    <form onSubmit={handleSubmit}>
                        {/* Error Message */}
                        {error && (
                            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                {error}
                            </div>
                        )}

                        {/* Survey Selection */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Enquêtes <span className="text-red-600">*</span>
                            </label>
                            <SearchableMultiSelect
                                options={surveys}
                                selectedValues={formData.survey_ids}
                                onChange={(selectedIds) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        survey_ids: selectedIds
                                    }));
                                    setError('');
                                }}
                                placeholder="Rechercher et sélectionner des enquêtes..."
                                searchPlaceholder="Rechercher par nom ou ID..."
                                loading={loadingSurveys}
                                disabled={loadingSurveys}
                                maxHeight="300px"
                            />
                            <p className="mt-1 text-sm text-gray-500">
                                Sélectionnez les enquêtes pour ce lot de paiement. Le type (CATI ou Terrain) sera déterminé automatiquement selon les enquêtes sélectionnées.
                            </p>
                        </div>

                        {/* Period Selection */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de début <span className="text-red-600">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="period_start"
                                    value={formData.period_start}
                                    onChange={handleChange}
                                    max={getMaxEndDate()}
                                    required
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                                        dateError && dateError.includes('début') ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                    }`}
                                />
                                {dateError && dateError.includes('début') ? (
                                    <p className="mt-2 text-sm text-red-600 flex items-center">
                                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                                        {dateError}
                                    </p>
                                ) : (
                                    <p className="mt-1 text-xs text-gray-500">
                                        La date de début ne peut pas être aujourd'hui pour garantir que tous les travaux de la journée sont inclus dans le lot.
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de fin <span className="text-red-600">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="period_end"
                                    value={formData.period_end}
                                    onChange={handleChange}
                                    min={formData.period_start || undefined}
                                    max={getMaxEndDate()}
                                    required
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                                        dateError && (dateError.includes('fin') || dateError.includes('postérieure')) ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                    }`}
                                />
                                {dateError && (dateError.includes('fin') || dateError.includes('postérieure')) ? (
                                    <p className="mt-2 text-sm text-red-600 flex items-center">
                                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                                        {dateError}
                                    </p>
                                ) : (
                                    <p className="mt-1 text-xs text-gray-500">
                                        La date de fin ne peut pas être aujourd'hui pour garantir que tous les travaux de la journée sont inclus dans le lot.
                                    </p>
                                )}
                            </div>
                        </div>


                        {/* Submit Buttons */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 sm:space-x-0">
                            <button
                                type="button"
                                onClick={() => navigate('/payments')}
                                className="w-full sm:w-auto px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors duration-200"
                                disabled={loading}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                        Création en cours...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" />
                                        Créer le Lot
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Help Section - Collapsible */}
                <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
                    <button
                        onClick={() => setShowHowItWorks(!showHowItWorks)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                        <h3 className="text-lg font-semibold text-gray-900">
                            Comment ça marche ?
                        </h3>
                        <FontAwesomeIcon 
                            icon={showHowItWorks ? faChevronUp : faChevronDown} 
                            className="text-gray-500 transition-transform duration-200"
                        />
                    </button>
                    
                    {showHowItWorks && (
                        <div className="px-6 pb-6 border-t border-gray-100">
                            <div className="space-y-3 text-sm text-gray-700 pt-4">
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">1.</span>
                                    <p>
                                        Sélectionnez les enquêtes et la période de paiement. Le type (CATI ou Terrain) sera déterminé automatiquement selon les enquêtes sélectionnées. Les contrôleurs seront automatiquement inclus s'il y a des présences confirmées dans cette période.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">2.</span>
                                    <p>
                                        <strong>Pour les enquêtes Terrain (F2F) :</strong> Les prestataires sont payés pour le travail de leurs enquêteurs. Les enquêteurs F2F ne reçoivent pas de paiement direct - leur productivité est agrégée et payée à leur prestataire. Les prestataires reçoivent également le paiement pour leur propre travail de terrain.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">3.</span>
                                    <p>
                                        <strong>Pour les enquêtes CATI :</strong> Les enquêteurs CATI sont payés directement en fonction de leur productivité individuelle.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">4.</span>
                                    <p>
                                        Le système calculera automatiquement les montants à payer pour les enquêteurs CATI et les prestataires (pour Terrain) en fonction des données de productivité, et pour les contrôleurs en fonction de leurs présences confirmées dans cette période.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">5.</span>
                                    <p>
                                        Le lot sera créé automatiquement en statut "Brouillon" après création.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">6.</span>
                                    <p>
                                        Vous pourrez ensuite réviser, ajouter des ajustements (primes/pénalités), et utiliser l'option <strong>"Soldé"</strong> si nécessaire.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">7.</span>
                                    <p>
                                        <strong>Option "Soldé" :</strong> Par défaut, les paiements avec un montant net inférieur à 200 MAD sont automatiquement marqués comme "non payés" et exclus des exports. L'option "Soldé" permet d'inclure ces petits montants dans les exports. Lorsque "Soldé" est activé, tous les paiements (même ceux &lt; 200 MAD) seront inclus dans l'export. Lorsqu'il est désactivé, les paiements &lt; 200 MAD seront à nouveau marqués comme non payés.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">8.</span>
                                    <p>
                                        Après révision, vous pouvez verrouiller le lot, qui attendra ensuite la validation par l'administrateur général ou ceux délégués par lui.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">9.</span>
                                    <p>
                                        Après validation, vous ou le responsable administratif pourrez exporter les fichiers par méthode de paiement.
                                    </p>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-semibold mr-2">10.</span>
                                    <p>
                                        Vient ensuite la confirmation des paiements effectués par le responsable administratif.
                                    </p>
                                </div>
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-4">
                                    <div className="flex items-start">
                                        <span className="font-semibold text-orange-600 mr-2"> Attention</span>
                                        <p className="text-orange-700 font-medium">
                                            Impossible de créer un lot avec les mêmes enquêtes et des dates qui se chevauchent. Choisissez des dates différentes et/ou des enquêtes différentes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                message={successMessage}
            />
        </div>
    );
};

export default CreatePaymentBatch;

